import {useSyncExternalStore} from 'react'
import {getAirports} from '@/utils/airports'

/**
 * THE SERVER'S ANSWER TO "FROM / TO AIRPORT?", REMEMBERED PER PICKUP + DROP-OFF PAIR.
 *
 * With the admin switch "Calculate trip type on the server" ON, `/trips/calculate-route`
 * (and `/calculate-cost`) return `trip_direction`, `trip_direction_source: 'zone'` and
 * `airport: {id, code, name} | null`, decided from the admin-drawn airport zones. The server
 * ignores the direction we send and stores its own, and the flight-number requirement is
 * checked against ITS direction — so the flight step, policies and airport screens must
 * follow it, not our own `isNearAirport` guess.
 *
 * With the switch OFF the same responses carry `trip_direction: null`: nothing is recorded
 * and every caller falls back to the client calculation, exactly as before.
 *
 * Kept in a tiny store (not React state) because the direction is read in hooks, plain
 * services and storage-driven screens alike. Keyed on the two coordinates that decide it —
 * pickup, then drop-off — so a changed address can never reuse a stale answer.
 */
const answers = new Map()
const listeners = new Set()
let version = 0

const coord = (point) => {
    const lat = Number(point?.lat)
    const lng = Number(point?.lng)
    return Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(5)},${lng.toFixed(5)}` : null
}
const pairKey = (origin, destination) => {
    const a = coord(origin)
    const b = coord(destination)
    return a && b ? `${a}|${b}` : null
}

const notify = () => {
    version += 1
    listeners.forEach((listener) => listener())
}

/** Record (or clear, when the server sent null) the direction for this pickup/drop-off pair. */
export function recordServerDirection(origin, destination, data) {
    const key = pairKey(origin, destination)
    if (!key) return
    const direction = data?.trip_direction
    const had = answers.has(key)
    if (direction) {
        answers.set(key, {direction: String(direction), airport: data?.airport || null})
        notify()
    } else if (had) {
        answers.delete(key)
        notify()
    }
}

/** `{direction, airport}` the server decided for this pair, or `null` (switch off / unknown). */
export function serverDirectionFor(origin, destination) {
    const key = pairKey(origin, destination)
    return key ? (answers.get(key) || null) : null
}

/** Our `/config/airports` record for the airport the server named, or `null`. */
export function airportRecordFor(serverAirport) {
    if (!serverAirport) return null
    return getAirports().find((airport) => (
        (serverAirport.id != null && String(airport?.id) === String(serverAirport.id))
        || (serverAirport.code && airport?.code === serverAirport.code)
    )) || null
}

const subscribe = (listener) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

/** Re-renders a component when a server direction is recorded or cleared. */
export function useServerDirectionVersion() {
    return useSyncExternalStore(subscribe, () => version, () => 0)
}
